<template>
  <v-container fill-height fluid class="pa-0">
    <v-card class="flexcard">
      <v-toolbar dense>
        <v-card-title>
          <span class="headline">Upload content</span>
        </v-card-title>
      </v-toolbar>
      <v-card-text class="grow">
        <v-row fill-height>
        <!-- list files -->
        <v-col cols="12" class="pt-0 pb-0">
          <UploadList :files="files" :processing="processing" v-if="files.length > 0"/>
        </v-col>
        <v-col cols="12" class="pt-0 pb-0" grow @click="clickHandler">
          <input
            v-show="false"
            ref="input"
            type="file"
            multiple
            @change="fileChanged"
          >
          <drop
            class="drop"
            @drop="handleDrop"
          >
            <v-col align-self-center>
              <v-icon large color="black">mdi-download</v-icon><br>
            <span class="headline">
            Select <br>or<br> drag files here
            </span>
          </v-col>
          </drop>
        </v-col>
        <template v-if="files.length > 0">
          <v-col cols="12" class="pb-0">
            <span class="subtitle-1 font-weight-regular">
              FFmpeg task:
            </span>
          </v-col>
          <v-col cols="12" class="pt-0 pb-0">
            <v-radio-group v-model="outputFormat" class="pt-0 pb-0 mt-0">
              <v-radio
                v-for="(n, index) in outputFormats"
                :key="index"
                :label="n.label"
                :value="n.val"
                color="blue darken-2"
              ></v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="4" class="pt-0 pb-0">
            <span class="subtitle-1 font-weight-regular">
              Start time (0-60 seconds)
            </span>
          </v-col>
          <v-col cols="8" class="pt-0 pb-0">
            <v-slider
              v-model="startTime"
              thumb-label="always"
              thumb-size="20"
              max="60"
              color="blue darken-2"
              track-color="blue darken-2"
            ></v-slider>
          </v-col>
          <v-col cols="4" class="pt-0 pb-0">
            <span class="subtitle-1 font-weight-regular">
              Interval (1-60 seconds)
            </span>
          </v-col>
          <v-col cols="8" class="pt-0 pb-0">
            <v-slider
              v-model="frameInterval"
              thumb-label="always"
              thumb-size="20"
              min="1"
              max="60"
              color="blue darken-2"
              track-color="blue darken-2"
            ></v-slider>
          </v-col>
        </template>
        <v-col cols="12" grow class="instructions">
          <span class="subheading font-weight-light">
            You can upload content to your account.<br>
            Select files from your device.<br>
            Click 'UPLOAD FILES' when you are ready to store them to cloud.<br>
            Leave the page open while upload is in progress.<br>
          </span>
        </v-col>
        <v-col cols="12">
          <v-row justify="space-around">
            <v-btn
            rounded
            color="success"
            @click="processFiles"
            :disabled="uploadDisabled"
            >
              Upload files
            </v-btn>
            <v-btn
              rounded
              color="warning"
              @click="cancel"
              :disabled="!!processing"
            >
              close
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
// eslint-disable
import { Drop } from 'vue-drag-drop';
import S3 from 'aws-sdk/clients/s3';
import UploadList from '@/components/Content/UploadList.vue';
import { idToken } from '@/utils/auth';
import resolveMime from '@/utils/mimeResolver';

export default {
  components: {
    Drop,
    UploadList,
  },
  name: 'UploadContentDialog',
  data() {
    return {
      error: null,
      processing: 0,
      progress: 0,
      outputFormat: 'png',
      outputFormats: [
        {
          val: 'jpg',
          label: 'JPG (smaller file size, lossy format)',
        },
        {
          val: 'png',
          label: 'PNG (bigger file size, lossless format)',
        },
      ],
      frameInterval: 10,
      startTime: 0,
      files: [],
    };
  },
  computed: {
    uploadDisabled() {
      const notUploaded = this.files.filter((el) => !el.uploaded);
      if (this.processing > 0 || notUploaded.length === 0) return true;
      return false;
    },
  },
  methods: {
    async getUploadPermissions(mime, name) {
      const token = await idToken();
      const response = await fetch('https://rest.boom.livesynccloud.com/api/permissions', {
        method: 'post',
        mode: 'cors',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mime, name }),
      });
      const json = await response.json();
      return json;
    },
    cancel() {
      this.files = [];
      this.startTime = 0;
      this.frameInterval = 10;
      this.outputFormat = 'png';
      this.$emit('update:dialog', false);
    },
    clickHandler() {
      if (this.processing !== 0) {
        alert('try again when previous upload is done.');
        return;
      }
      this.$refs.input.click();
    },
    async processFiles() {
      // Get list of video files to be uploaded
      const videoFileNames = this.files
        .filter((item) => item.type === 'video/mp4')
        .map((item) => (item.name));

      // Add task definition files to uploadable files
      this.files = this.files.concat(this.createTaskDefinitionJson(videoFileNames));

      // eslint-disable-next-line
      for (const item of this.files) {
        // eslint-disable-next-line
        await this.processFile(item);
      }
      this.processing = 0;
      this.progress = 0;
    },
    createTaskDefinitionJson(names) {
      const { startTime, frameInterval, outputFormat } = this;
      const files = [];
      names.forEach((video) => {
        // Create task definition
        const taskDefinition = {
          video,
          startTime,
          frameInterval,
          outputFormat,
        };
        // Create task defnition file name
        const name = `${video.substr(0, video.lastIndexOf('.'))}.json`;
        // Create file object
        const file = new File([JSON.stringify(taskDefinition, null, 2)], name, {
          type: 'application/json',
        });
        // Add to array
        files.push({
          name,
          file,
          progress: 0,
          stateVal: 0,
          type: resolveMime(file),
        });
      });
      return files;
    },
    async processFile(data) {
      // Set initial value for progress & processingState
      try {
        this.progress = 0;
        this.processing = 1;
        if (data.uploaded) return;

        // Set initial value for progress & processingState
        this.progress = 0;
        this.processing = 2;
        // eslint-disable-next-line
        data.state = 'Getting upload permissions...';
        // eslint-disable-next-line
        data.stateVal = 0;

        const permissions = await this.getUploadPermissions(data.type, data.name);

        if (permissions.sts) {
          // eslint-disable-next-line
          data.state = 'Uploading file...';
          // Get permissions
          // eslint-disable-next-line
          data.progress = 0;
          // eslint-disable-next-line
          data.stateVal = 1;
          await this.uploadFile({ permissions, data });
        } else {
          // eslint-disable-next-line
          data.progress = 100;
          // eslint-disable-next-line
          data.stateVal = 1;
        }
        // eslint-disable-next-line
        data.state = 'File uploaded';
        // eslint-disable-next-line
        data.stateVal = 2;
        // eslint-disable-next-line
        data.uploaded = true;

        this.progress = 0;
        this.processing = 1;
      } catch (e) {
        const msg = e.message || e;
        // eslint-disable-next-line
        data.error = true;
        // eslint-disable-next-line
        data.uploaded = true;
        // eslint-disable-next-line
        data.stateVal = 2;
        // eslint-disable-next-line
        data.state = msg;
      }
    },

    uploadFile(obj) {
      // Resolve immediately if nothing to upload
      if (!obj) {
        return Promise.resolve();
      }
      const { permissions, data } = obj;
      const { fields, sts } = permissions;
      const { file, name } = data;

      // Set credentials and Region
      const s3 = new S3({
        apiVersion: '2006-03-01',
        region: sts.region,
        accessKeyId: sts.accessKeyId,
        secretAccessKey: sts.secretAccessKey,
        sessionToken: sts.sessionToken,
      });

      const params = {
        Bucket: sts.bucket,
        Key: `${fields.key}${name}`,
        Body: file,
        ContentType: fields.ContentType,
      };

      const options = { partSize: 10 * 1024 * 1024 };
      const upload = s3.upload(params, options);
      upload.on('httpUploadProgress', (evt) => {
        data.progress = Math.floor((evt.loaded * 100) / evt.total);
      });
      const promise = upload.promise();
      return promise;
    },

    fileChanged(evt) {
      const { length } = evt.target.files;
      const files = [];
      let i = 0;
      for (; i < length; i += 1) {
        files.push({
          name: evt.target.files[i].name,
          file: evt.target.files[i],
          progress: 0,
          stateVal: 0,
          type: resolveMime(evt.target.files[i]),
        });
      }
      this.files = this.files.concat(files);

      // Reset target value so change event is triggered again if same file is added
      /* eslint-disable-next-line */
      evt.target.value = null;
    },

    handleDrop(data, event) {
      if (this.processing !== 0) {
        alert('try again when previous upload is done.');
        return;
      }
      event.preventDefault();
      const { files } = event.dataTransfer;
      const { length } = files;
      const computedFiles = [];
      let i = 0;
      for (; i < length; i += 1) {
        computedFiles.push({
          name: files.item(i).name,
          file: files.item(i),
          360: true,
          progress: 0,
          stateVal: 0,
          type: resolveMime(files.item(i)),
        });
      }
      this.files = this.files.concat(computedFiles);
    },
  },
};
</script>

<style scoped>
.flexcard {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: #f0f0f0;
}
.drag, .drop {
  box-sizing: border-box;
  display: flex;
  height: 100%;
  background: #ccc;
  vertical-align: middle;
  text-align: center;
  color: #000;
  cursor: pointer;
}
.drag {
  color: #fff;
  cursor: move;
  background: #777;
  border-right: 2px solid #666;
  border-bottom: 2px solid #666;
}
.drop {
  background: #eee;
  border-top: 2px solid #ccc;
  border-left: 2px solid #ddd;
}
.instructions {
  text-align: center;
}
</style>
